import * as React from 'react'
require('../base.module.scss')
import { BaseComponentProps } from '../Common/extractClassNamesFromProps'
import {
    extractClassNamesFromProps
} from '../Common/extractClassNamesFromProps'
import omit from '../Common/omit'
import { Icon, IconType } from '../Icon/Icon'

import * as st from './Button.module.scss'

export const Default = Symbol('Default')
export const Primary = Symbol('Primary')
export const Prime = Symbol('Prime')
export const Playback = Symbol('Playback')
export const Purchase = Symbol('Purchase')
export const Transparent = Symbol('Transparent')
export const Translucent = Symbol('Translucent')

export interface SpecialButtonProps extends BaseComponentProps {
    disabled?: boolean,
    noWrap?: boolean,
    href?: string,
    type?: string,
    useIcon?: IconType,
    forceDoubleHeight?: boolean,
    status?: string,
    role?: string,
    tabIndex?: number|string,
    name?: string,
    onClick?: Function,
    target?: string,
    rel?: string
    buttonRef?: Function
}

/**
 * The only true button.
 */
export interface ButtonProps extends SpecialButtonProps {
    /** Default | Primary | Prime | Purchase */
    use?: Symbol
}
export class Button extends React.Component<ButtonProps> {
  displayName = 'Button'

  constructor(props: ButtonProps) {
    super(props)
  }
  render() {
    const { props } = this
    const Tag = props.href ? 'a' : 'button'
    const use = props.use || Default
    const noWrap = props.noWrap || false
    const forceDoubleHeight = props.forceDoubleHeight || false
    const useIcon = props.useIcon

    const className = extractClassNamesFromProps(
      props, {
        [st.noWrap]: noWrap,
        [st.forceDouble]: forceDoubleHeight,
        [st.withIcons]: Boolean(useIcon),

        // button styles
        [st.base]: use === Default,
        [st.primary]: use === Primary,
        [st.prime]: use === Prime,
        [st.playback]: use === Playback,
        [st.purchase]: use === Purchase,
        [st.transparent]: use === Transparent,
        [st.translucent]: use === Translucent,
      },
      st.button
    )

    const filteredProps = omit(Object.assign({}, props, {
      ...(props.buttonRef && {ref: props.buttonRef})
    }), [
      'noWrap',
      'useIcon',
      'forceDoubleHeight',
      'use',
      'buttonRef'
    ])

    return !useIcon ? (
      <Tag aria-disabled={props.disabled} {...filteredProps} className={className} />
    ) : (
      <Tag aria-disabled={props.disabled} {...filteredProps} className={className}>
          <Icon use={useIcon} />
        <span className={st.content}>{filteredProps.children}</span>
      </Tag>
    )
  }
}

export const PrimaryButton: React.ComponentType<SpecialButtonProps> = props => (
    <Button use={Primary} {...props} />
)

export const PrimeButton: React.ComponentType<SpecialButtonProps> = props => (
    <Button use={Prime} {...props} />
)

export const PurchaseButton: React.ComponentType<SpecialButtonProps> = props => <Button use={Purchase} {...props} />

export const WatchlistButton = (props:SpecialButtonProps) => {
    const status = props.status
    const use =
      status === 'added' ? IconType.TICK
        : status === 'progress' ? IconType.SPINNER
          : IconType.ADD

    return <Button {...props} useIcon={use} use={Translucent} />
  }

// Temp hack until we move to clientside React rendering and drop AUI DOM update
export const VeryHackyWatchlistButtonForAuiUpdate = (props:any) => {
    const status = props.status
    const statusClass = status === 'added' ? 'dv-wl-toggle-added' : 'dv-wl-toggle-add'
    const className = [props.className, statusClass].join(' ')

    return <Button {...props} use={Translucent} className={className} forceDoubleHeight>
      <Icon use={IconType.TICK} className="dv-wl-toggle-icon dv-wl-toggle-icon-added" />
      <Icon use={IconType.ADD} className="dv-wl-toggle-icon dv-wl-toggle-icon-add" />
      <Icon use={IconType.SPINNER} className="dv-wl-toggle-icon dv-wl-toggle-icon-progress" />
      <span className={st.content}>{props.children}</span>
    </Button>
  }

// Add displayName to preserve the component name on snapshot testing
PrimaryButton.displayName = 'PrimaryButton'
PrimeButton.displayName = 'PrimeButton'
PurchaseButton.displayName = 'PurchaseButton'
